import { DataNode, ScrollableTreeRef } from 'components/WorkspaceEditor/types'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { useEffect, useRef } from 'react'
import { useScrollStoreShallowSelector } from '../store/scrollStore'
import { isValidTreeRef } from '../utils/TreeNodesUtils'
import { useAttemptScrollWithCurrentContent } from './useAttemptScrollWithCurrentContent'
import { useHandleActiveTab } from './useHandleActiveTab'
import { useHandleLatestContentItem } from './useHandleLatestContentItem'
import { useHandleScrollToRequestedTreeNode } from './useHandleScrollToRequestedTreeNode'
import { usePendingScrollRequest } from './usePendingScrollRequest'
import { useScrollToContent } from './useScrollToContent'

/**
 * Hook for scrolling to a specific tree node in the workspace.
 * This hook manages the scrolling logic based on the current
 * scroll request, expanded keys, and tree data.
 * @param treeRef - ref to the Ant Tree component (should be passed to <Tree ref={treeRef} />)
 * @param treeData - current tree data
 * @returns {void}
 */
export const useScrollToTreeNode = (
    treeRef: React.MutableRefObject<ScrollableTreeRef>,
    treeData: DataNode[]
) => {
    const {
        expandedKeys,
        setExpandedKeys,
        contentItemViewNodes,
    } = useShallowSelector((state) => ({
        expandedKeys: state.expandedKeys,
        setExpandedKeys: state.actions.setExpandedKeys,
        contentItemViewNodes: state.contentItemViewNodes,
    }))
    const {
        scrollToContent,
        clearScrollToContent,
        setPendingScrollRequest,
    } = useScrollStoreShallowSelector((state) => ({
        scrollToContent: state.scrollToContent,
        clearScrollToContent: state.clearScrollToContent,
        setPendingScrollRequest: state.setPendingScrollRequest,
    }))
    const handleLatestContentItem = useHandleLatestContentItem()
    const handleTrackChangeFromActiveTab = useHandleActiveTab()

    const scrollToElement = useScrollToContent(
        treeRef,
        expandedKeys,
        setExpandedKeys,
        treeData
    )

    const attemptScrollWithCurrentContent = useAttemptScrollWithCurrentContent(
        contentItemViewNodes,
        scrollToElement,
        treeData
    )

    usePendingScrollRequest(treeData, attemptScrollWithCurrentContent)

    const handleTrackChangeFromActiveTabRef = useRef(
        handleTrackChangeFromActiveTab
    )
    const handleLatestContentItemRef = useRef(handleLatestContentItem)
    const scrollToElementRef = useRef(scrollToElement)
    const clearScrollToContentRef = useRef(clearScrollToContent)

    handleTrackChangeFromActiveTabRef.current = handleTrackChangeFromActiveTab
    handleLatestContentItemRef.current = handleLatestContentItem
    scrollToElementRef.current = scrollToElement
    clearScrollToContentRef.current = clearScrollToContent

    const handleScrollToRequestedTreeNode = useHandleScrollToRequestedTreeNode(
        scrollToContent,
        handleTrackChangeFromActiveTabRef,
        handleLatestContentItemRef,
        scrollToElementRef,
        clearScrollToContentRef,
        setPendingScrollRequest
    )

    useEffect(() => {
        if (
            !scrollToContent?.id ||
            !scrollToContent?.correspondingTrackChange
        ) {
            return
        }

        const hasValidTreeRef = isValidTreeRef(treeRef)
        if (!hasValidTreeRef || !treeData.length) {
            return
        }

        handleScrollToRequestedTreeNode()
    }, [scrollToContent, handleScrollToRequestedTreeNode])
}
